import React, { createContext, useState, useEffect, useContext } from 'react';
import * as api from '../services/api';

const NewsContext = createContext();

export const NewsProvider = ({ children }) => {
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadNews();
    }, []);

    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await api.getNews();
            // Trust the backend order (which supports reordering)
            setNews(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to load news:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const addNews = async (item) => {
        try {
            const newItem = await api.createNews(item);
            setNews(prev => [newItem, ...prev]);
            return newItem;
        } catch (err) {
            console.error("Failed to add news:", err);
            throw err;
        }
    };

    const updateNews = async (id, updatedItem) => {
        try {
            const result = await api.updateNews(id, updatedItem);
            setNews(prev => prev.map(n => n.id === id ? result : n));
            return result;
        } catch (err) {
            console.error("Failed to update news:", err);
            throw err;
        }
    };

    const deleteNews = async (id) => {
        try {
            await api.deleteNews(id);
            setNews(prev => prev.filter(n => n.id !== id));
        } catch (err) {
            console.error("Failed to delete news:", err);
            throw err;
        }
    };

    const reorderNews = async (newOrderIds) => {
        try {
            // Optimistic update
            const idToIndex = new Map(newOrderIds.map((id, index) => [id, index]));
            const sortedNews = [...news].sort((a, b) => {
                const indexA = idToIndex.get(a.id);
                const indexB = idToIndex.get(b.id);
                // If one is not in the list (shouldn't happen), keep original relative order or put at end
                if (indexA === undefined) return 1;
                if (indexB === undefined) return -1;
                return indexA - indexB;
            });
            
            setNews(sortedNews);

            await api.reorderNews(newOrderIds);
        } catch (err) {
            console.error("Failed to reorder news:", err);
            // Revert on error? For now just log.
            loadNews(); // Reload to get server state
            throw err;
        }
    };

    const getNewsById = (id) => {
        return news.find(n => n.id === parseInt(id));
    };

    const publishedNews = news.filter(n => n.published !== false); // Default to true if undefined

    return (
        <NewsContext.Provider value={{ news, publishedNews, loading, error, addNews, updateNews, deleteNews, getNewsById, reorderNews }}>
            {children}
        </NewsContext.Provider>
    );
};

export const useNews = () => useContext(NewsContext);
